import React from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  GridItem,
  Text,
  Button,
  AspectRatio,
  Flex,
} from '@chakra-ui/react';
import { DeptGrid } from '@rsc/ui-components/dept/design-system';
import SimpleSlider from '@rsc/ui-components/dept/molecules/SimpleSlider';
import { generateSectionId } from '@rsc/ui-components/dept/utils/generateSectionId';
import Link from '@rsc/ui-components/tailwind/Link';
import getLink, { getCollectionLink } from '@rsc/utils/getLink';
import KeyCollectionsItem from './KeyCollectionsItem';
import SanityBackgroundImage from '../../../atoms/SanityBackgroundImage';

function CarouselRowKeyCollections({ block }) {
  return (
    <Container {...generateSectionId(block)} my={10}>
      <Text as="h2" textStyle="h2" fontWeight="bold" pb={6}>
        {block.title}
      </Text>
      {block.collections?.map(group => {
        const groupTitle = group?.collectionReference?.store?.title || '';

        return (
          <DeptGrid py={10} key={group._key} alignItems="center">
            <GridItem
              colSpan={{ base: 6, lg: 3 }}
              display={['none', null, 'block']}
            >
              <Text as="h3" textStyle="h3" mb={2} mt={6}>
                {groupTitle.split('/').at(-1)}
              </Text>
              <Text textStyle="body" mb={6}>
                {group.description}
              </Text>
              {group.linkText && (
                <Button
                  as={Link}
                  link={getCollectionLink(groupTitle)}
                  variant="primary"
                  mb={6}
                >
                  {group.linkText}
                </Button>
              )}
            </GridItem>

            <GridItem
              colSpan={{ base: 6, lg: 9 }}
              display={{ base: 'none', md: 'block' }}
            >
              <SimpleSlider
                items={group.collections}
                sliderItem={KeyCollectionsItem}
              />
            </GridItem>
            <GridItem colSpan={6} display={['block', null, 'none']}>
              <AspectRatio ratio={16 / 9}>
                <SanityBackgroundImage
                  gradientGenerator={url =>
                    `linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(360deg, rgba(0, 0, 0, 0.80) 0%, rgba(0, 0, 0, 0.00) 100%), url(${url}), lightgray 0px -0.1px / 100% 106.63% no-repeat`
                  }
                  image={group.mobileImage}
                  borderRadius="lg"
                  display="flex"
                  justifyContent="flex-start !important"
                  alignItems="flex-end !important"
                  as={Link}
                  link={getCollectionLink(groupTitle)}
                >
                  <Text as="h2" textStyle="h2" color="typography.lighter" p={6}>
                    {groupTitle?.split('/').at(-1)}
                  </Text>
                </SanityBackgroundImage>
              </AspectRatio>
              <Flex overflow="scroll" py={4}>
                {group.collections.map(collection => (
                  <Button
                    as={Link}
                    className="text-brand-foreground mr-2 border-primary"
                    key={collection._key}
                    link={
                      getLink(
                        collection?.cta?.linkType,
                        collection?.cta?.href,
                        collection?.cta?.internalLink
                      ) ||
                      getCollectionLink(
                        collection.collectionReference?.store?.title
                      )
                    }
                    variant="tertiary"
                    minWidth="auto"
                  >
                    {collection?.title ||
                      collection?.collectionReference?.store?.title
                        .split('/')
                        .at(-1)}
                  </Button>
                ))}
              </Flex>
              {group.linkText && (
                <Button
                  as={Link}
                  className="mb-6 w-full"
                  link={getCollectionLink(groupTitle)}
                  variant="primary"
                >
                  {group.linkText}
                </Button>
              )}
            </GridItem>
          </DeptGrid>
        );
      })}
    </Container>
  );
}

export default CarouselRowKeyCollections;

CarouselRowKeyCollections.propTypes = {
  block: PropTypes.shape({
    title: PropTypes.string,
    collections: PropTypes.arrayOf(
      PropTypes.shape({
        _key: PropTypes.string,
        collectionReference: PropTypes.shape({
          store: PropTypes.shape({
            title: PropTypes.string,
          }),
        }),
        collections: PropTypes.arrayOf(
          PropTypes.shape({
            collectionReference: PropTypes.shape({
              store: PropTypes.shape({
                title: PropTypes.string,
              }),
            }),
          })
        ),
        linkText: PropTypes.string,
        mobileImage: PropTypes.shape({
          asset: PropTypes.shape({
            url: PropTypes.string,
          }),
          hotspot: PropTypes.shape({
            x: PropTypes.number,
            y: PropTypes.number,
          }),
        }),
        description: PropTypes.string,
      })
    ),
  }).isRequired,
};
